import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Building2, Users, Download, BarChart4, Calendar, Filter, FileText, DollarSign, TrendingUp, Clock, Activity } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/components/AuthProvider';
import { Badge } from '@/components/ui/badge';
import { collection, getDocs, query, where, orderBy, limit, getDoc, doc, Firestore } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { MerchantApplication } from '@/features/merchant-application/types';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import { toast } from '@/components/ui/use-toast';
import { useImpersonation } from '@/hooks/useImpersonation';

interface CompanyData {
  id: string;
  name: string;
  email: string;
  status: string;
  createdAt: Date;
  ownerId: string;
  ownerName?: string;
  ownerEmail?: string;
  userCount: number;
  invoiceCount: number;
  revenueTotal: number;
  totalDonationsAmount: number;
}

interface UserData {
  id: string;
  email: string;
  displayName: string;
  role: string;
}

const TenantDashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [isAuthorized, setIsAuthorized] = useState(false);

  // Impersonation
  const { startImpersonation, isImpersonating: isImpersonationActive } = useImpersonation();
  const [impersonatingTenantId, setImpersonatingTenantId] = useState<string | null>(null);

  // Check for super_admin role - only super_admin can access tenant features
  useEffect(() => {
    if (user) {
      if (user.role === 'super_admin') {
        setIsAuthorized(true);
      } else {
        // Redirect to main dashboard if not super_admin
        navigate('/app');
      }
    }
  }, [user, navigate]);

  // Always define the query function regardless of authorization
  const fetchTenantsData = async () => {
    if (!user || user.role !== 'super_admin') {
      return [];
    }

    const tenantsData: CompanyData[] = [];
      
    // Build query
    let tenantsQuery = query(
      collection(db, 'companies'),
      orderBy(sortBy === 'name' ? 'name' : 'createdAt', sortBy === 'oldest' ? 'asc' : 'desc'),
      limit(10)
    );
    
    if (statusFilter !== 'all') {
      tenantsQuery = query(tenantsQuery, where('status', '==', statusFilter));
    }
    
    const snapshot = await getDocs(tenantsQuery);
    
    // Process companies
    const promises = snapshot.docs.map(async (docSnap) => {
      const company = docSnap.data();
      
      // Get owner info if available
      let ownerData: UserData | null = null;
      if (company.ownerId) {
        const ownerSnap = await getDoc(doc(db, 'users', company.ownerId));
        if (ownerSnap.exists()) {
          ownerData = ownerSnap.data() as UserData;
        }
      }
      
      // Get user count
      const usersQuery = query(
        collection(db, 'users'),
        where('companyId', '==', docSnap.id)
      );
      const usersSnapshot = await getDocs(usersQuery);
      
      // Get invoice count
      const invoicesQuery = query(
        collection(db, 'invoices'),
        where('companyId', '==', docSnap.id)
      );
      const invoicesSnapshot = await getDocs(invoicesQuery);
      
      // Calculate total revenue
      let totalRevenue = 0;
      invoicesSnapshot.docs.forEach(doc => {
        const invoice = doc.data();
        if (invoice.status === 'paid') {
          totalRevenue += invoice.total || 0;
        }
      });
      
      // Calculate total donations amount
      let totalDonations = 0;
      try {
        const donationsQuery = query(
          collection(db, 'donations'),
          where('companyId', '==', docSnap.id),
          where('status', '==', 'completed')
        );
        const donationsSnapshot = await getDocs(donationsQuery);
        donationsSnapshot.docs.forEach(doc => {
          const donation = doc.data();
          totalDonations += donation.amount || 0;
        });
      } catch (donationsError) {
        console.error(`Error fetching donations for company ${docSnap.id}:`, donationsError);
        // Keep totalDonations as 0 if there's an error
      }
      
      // Add to results
      tenantsData.push({
        id: docSnap.id,
        name: company.name || 'Unnamed Company',
        email: company.email || 'No email',
        status: company.status || 'active',
        createdAt: company.createdAt?.toDate() || new Date(),
        ownerId: company.ownerId || '',
        ownerName: ownerData?.displayName || 'No owner',
        ownerEmail: ownerData?.email || '',
        userCount: usersSnapshot.size,
        invoiceCount: invoicesSnapshot.size,
        revenueTotal: totalRevenue,
        totalDonationsAmount: totalDonations
      });
    });
    
    await Promise.all(promises);
    
    // Filter by search query if provided
    if (searchQuery) {
      const lowerSearch = searchQuery.toLowerCase();
      return tenantsData.filter(tenant => 
        tenant.name.toLowerCase().includes(lowerSearch) ||
        tenant.email.toLowerCase().includes(lowerSearch) ||
        tenant.ownerName?.toLowerCase().includes(lowerSearch) ||
        tenant.ownerEmail?.toLowerCase().includes(lowerSearch)
      );
    }
    
    return tenantsData;
  };

  // Always call useQuery, but use the 'enabled' option to control when it runs
  const { data: tenants, isLoading, error: _error } = useQuery({
    queryKey: ['tenants', searchQuery, statusFilter, sortBy, currentPage],
    queryFn: fetchTenantsData,
    enabled: !!user // Only run the query when user data is available
  });

  // Always define summary query function regardless of authorization
  const fetchSummaryData = async () => {
    if (!user || user.role !== 'super_admin') {
      return {
        totalTenants: 0,
        activeTenants: 0,
        totalUsers: 0,
        totalRevenue: 0,
        totalMerchantApplications: 0,
        pendingApplications: 0,
        approvedApplications: 0,
        monthlyPipelineRevenue: 0
      };
    }

    // Get total tenants
    const tenantsQuery = query(collection(db, 'companies'));
    const tenantsSnapshot = await getDocs(tenantsQuery);
    
    // Get active tenants
    const activeTenantsQuery = query(
      collection(db, 'companies'),
      where('status', '==', 'active')
    );
    const activeTenantsSnapshot = await getDocs(activeTenantsQuery);
    
    // Get total users
    const usersQuery = query(collection(db, 'users'));
    const usersSnapshot = await getDocs(usersQuery);
    
    // Get total revenue
    const invoicesQuery = query(
      collection(db, 'invoices'),
      where('status', '==', 'paid')
    );
    const invoicesSnapshot = await getDocs(invoicesQuery);
    
    let totalRevenue = 0;
    invoicesSnapshot.docs.forEach(doc => {
      const invoice = doc.data();
      totalRevenue += invoice.total || 0;
    });

    // Get merchant applications data
    const merchantApplicationsQuery = query(collection(db, 'merchantApplications'));
    const merchantApplicationsSnapshot = await getDocs(merchantApplicationsQuery);
    const merchantApplications = merchantApplicationsSnapshot.docs.map(doc => doc.data());
    
    const totalMerchantApplications = merchantApplications.length;
    const pendingApplications = merchantApplications.filter(app => 
      app.status === 'in_progress' || app.status === 'submitted'
    ).length;
    const approvedApplications = merchantApplications.filter(app => 
      app.status === 'approved'
    ).length;
    const monthlyPipelineRevenue = approvedApplications * 250; // $250 per approved merchant
    
    return {
      totalTenants: tenantsSnapshot.size,
      activeTenants: activeTenantsSnapshot.size,
      totalUsers: usersSnapshot.size,
      totalRevenue,
      totalMerchantApplications,
      pendingApplications,
      approvedApplications,
      monthlyPipelineRevenue
    };
  };

  // Always call useQuery for summary data, but use the 'enabled' option 
  const { data: summaryData } = useQuery({
    queryKey: ['tenants-summary'],
    queryFn: fetchSummaryData,
    enabled: !!user // Only run the query when user data is available
  });

  // Fetch merchant applications for pipeline overview and recent activity
  const fetchMerchantApplicationsData = async (): Promise<MerchantApplication[]> => {
    if (!user || user.role !== 'super_admin') {
      return [];
    }

    const merchantApplicationsQuery = query(collection(db, 'merchantApplications'));
    const snapshot = await getDocs(merchantApplicationsQuery);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as MerchantApplication));
  };

  const { data: merchantApplications = [] } = useQuery<MerchantApplication[]>({
    queryKey: ['merchantApplications', 'dashboard'],
    queryFn: fetchMerchantApplicationsData,
    enabled: !!user
  });

  // Calculate pipeline statistics
  const pipelineStats = React.useMemo(() => {
    const statusCounts: Record<string, number> = {
      lead: 0,
      phone: 0,
      offer: 0,
      underwriting: 0,
      documents: 0,
      approved: 0,
    };

    merchantApplications.forEach(app => {
      if (app.pipelineStatus && statusCounts[app.pipelineStatus] !== undefined) {
        statusCounts[app.pipelineStatus]++;
      }
    });

    const total = merchantApplications.length || 1;

    return Object.entries(statusCounts).map(([status, count]) => ({
      status,
      count,
      percentage: (count / total) * 100,
    }));
  }, [merchantApplications]);

  // Generate recent activities from merchant applications
  const recentActivities = React.useMemo(() => {
    const activities = [];
    
    merchantApplications.slice(0, 8).forEach(app => {
      if (app.submittedAt) {
        activities.push({
          id: `${app.id}-submitted`,
          type: 'application_submitted',
          applicationId: app.id,
          businessName: app.data?.corporationInfo?.businessName || 'Unknown Business',
          description: 'Application submitted',
          timestamp: typeof app.submittedAt === 'object' && 'toDate' in app.submittedAt
            ? app.submittedAt.toDate()
            : new Date(app.submittedAt),
          icon: FileText,
          color: 'text-blue-600',
        });
      }
      
      if (app.updatedAt) {
        activities.push({
          id: `${app.id}-updated`,
          type: 'status_change',
          applicationId: app.id,
          businessName: app.data?.corporationInfo?.businessName || 'Unknown Business',
          description: `Status changed to ${app.pipelineStatus || 'unknown'}`,
          timestamp: typeof app.updatedAt === 'object' && 'toDate' in app.updatedAt
            ? app.updatedAt.toDate()
            : new Date(app.updatedAt),
          icon: Activity,
          color: 'text-purple-600',
        });
      }
    });

    return activities
      .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
      .slice(0, 8);
  }, [merchantApplications]);

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      lead: 'Lead',
      phone: 'Phone',
      offer: 'Offer',
      underwriting: 'Underwriting',
      documents: 'Documents',
      approved: 'Approved',
    };
    return labels[status] || status;
  };

  // Handle creating a new tenant
  const handleCreateTenant = () => {
    navigate('/app/tenants/create');
  };

  // Handle viewing tenant details
  const handleViewTenant = (tenantId: string) => {
    navigate(`/app/tenants/${tenantId}`);
  };

  // Handle impersonation
  const handleImpersonate = async (tenantId: string) => {
    if (isImpersonationActive || impersonatingTenantId) return;

    setImpersonatingTenantId(tenantId);
    try {
      const result = await startImpersonation(tenantId);
      if (!result.success) {
        toast({
          title: 'Impersonation failed',
          description: result.error || 'Unable to impersonate this tenant',
          variant: 'destructive'
        });
        setImpersonatingTenantId(null);
      }
      // On success, the hook redirects to /app
    } catch (error) {
      console.error('[TenantDashboard] Impersonation error:', error);
      toast({
        title: 'Impersonation failed',
        description: 'An unexpected error occurred',
        variant: 'destructive'
      });
      setImpersonatingTenantId(null);
    }
  };

  // If still loading user, show loading state
  if (!user) {
    return <div className="flex items-center justify-center h-64">
      <Skeleton className="h-8 w-8 rounded-full" />
    </div>;
  }

  // If not super_admin, don't render the dashboard
  if (!isAuthorized) {
    return null;
  }

  return (
    <div className="container mx-auto py-8 space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Tenant Management</h1>
          <p className="text-muted-foreground">
            Manage all tenant companies in your system
          </p>
        </div>
        <Button onClick={handleCreateTenant} className="flex items-center gap-2">
          <Plus className="h-4 w-4" />
          <span>Create New Tenant</span>
        </Button>
      </div>
      
      {/* Overview Cards - Tenant Metrics */}
      <div>
        <h2 className="text-xl font-semibold mb-4">Tenant Overview</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Total Tenants
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center">
                <Building2 className="mr-2 h-4 w-4 text-muted-foreground" />
                {summaryData ? (
                  <div className="text-2xl font-bold">{summaryData.totalTenants}</div>
                ) : (
                  <Skeleton className="h-8 w-16" />
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {summaryData?.activeTenants || 0} active ({summaryData ? 
                  Math.round((summaryData.activeTenants / summaryData.totalTenants) * 100) : 0}%)
              </p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Total Users
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center">
                <Users className="mr-2 h-4 w-4 text-muted-foreground" />
                {summaryData ? (
                  <div className="text-2xl font-bold">{summaryData.totalUsers}</div>
                ) : (
                  <Skeleton className="h-8 w-16" />
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Avg {summaryData && summaryData.totalTenants ? 
                  Math.round(summaryData.totalUsers / summaryData.totalTenants) : 0} users per tenant
              </p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Total Revenue
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center">
                <BarChart4 className="mr-2 h-4 w-4 text-muted-foreground" />
                {summaryData ? (
                  <div className="text-2xl font-bold">
                    ${summaryData.totalRevenue.toLocaleString()}
                  </div>
                ) : (
                  <Skeleton className="h-8 w-24" />
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                From all tenant invoices
              </p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Last 30 Days
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center">
                <Calendar className="mr-2 h-4 w-4 text-muted-foreground" />
                <div className="text-2xl font-bold">
                  +{summaryData ? 0 : <Skeleton className="h-8 w-12 inline-block" />}
                </div>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                New tenants this month
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Merchant Application Metrics */}
      <div>
        <h2 className="text-xl font-semibold mb-4">Merchant Application Pipeline</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Total Applications
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center">
                <FileText className="mr-2 h-4 w-4 text-muted-foreground" />
                {summaryData ? (
                  <div className="text-2xl font-bold">{summaryData.totalMerchantApplications}</div>
                ) : (
                  <Skeleton className="h-8 w-16" />
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                All time applications
              </p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Pending Applications
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center">
                <Clock className="mr-2 h-4 w-4 text-muted-foreground" />
                {summaryData ? (
                  <div className="text-2xl font-bold">{summaryData.pendingApplications}</div>
                ) : (
                  <Skeleton className="h-8 w-16" />
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {summaryData?.pendingApplications ? 'Awaiting review' : 'All caught up!'}
              </p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Pipeline Revenue
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center">
                <DollarSign className="mr-2 h-4 w-4 text-muted-foreground" />
                {summaryData ? (
                  <div className="text-2xl font-bold">
                    ${summaryData.monthlyPipelineRevenue.toLocaleString()}
                  </div>
                ) : (
                  <Skeleton className="h-8 w-24" />
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                From approved merchants
              </p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Approved This Month
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center">
                <TrendingUp className="mr-2 h-4 w-4 text-muted-foreground" />
                {summaryData ? (
                  <div className="text-2xl font-bold">{summaryData.approvedApplications}</div>
                ) : (
                  <Skeleton className="h-8 w-16" />
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Successfully approved
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Pipeline Overview and Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Pipeline Overview */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5" />
              Pipeline Overview
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {pipelineStats.map((stat) => (
              <div key={stat.status} className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium">{getStatusLabel(stat.status)}</span>
                  <span className="text-muted-foreground">
                    {stat.count} ({stat.percentage.toFixed(1)}%)
                  </span>
                </div>
                <Progress value={stat.percentage} className="h-2" />
              </div>
            ))}
            {pipelineStats.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4">
                No pipeline data available
              </p>
            )}
          </CardContent>
        </Card>

        {/* Recent Activity */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Recent Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {recentActivities.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">
                  No recent activity
                </p>
              ) : (
                recentActivities.map((activity) => {
                  const Icon = activity.icon;
                  return (
                    <div
                      key={activity.id}
                      className="flex items-start gap-4 p-3 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => navigate(`/app/merchant-applications`)}
                    >
                      <div className={`mt-0.5 ${activity.color}`}>
                        <Icon className="h-4 w-4" />
                      </div>
                      <div className="flex-1 space-y-1">
                        <p className="text-sm font-medium">
                          {activity.businessName}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {activity.description}
                        </p>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {activity.timestamp.toLocaleDateString()}
                      </span>
                    </div>
                  );
                })
              )}
            </div>
            {recentActivities.length > 0 && (
              <div className="mt-4 pt-4 border-t">
                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => navigate('/app/merchant-applications')}
                >
                  View All Applications
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      
      {/* Tabs for different views */}
      <Tabs defaultValue="all">
        <div className="flex items-center justify-between mb-4">
          <TabsList>
            <TabsTrigger value="all">All Tenants</TabsTrigger>
            <TabsTrigger value="active">Active</TabsTrigger>
            <TabsTrigger value="inactive">Inactive</TabsTrigger>
          </TabsList>
          
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                type="search"
                placeholder="Search tenants..."
                className="pl-8 w-[200px] lg:w-[300px]"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />
            </div>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="gap-1">
                  <Filter className="h-4 w-4" />
                  <span className="hidden sm:inline">Filter</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-[200px]">
                <DropdownMenuLabel>Filter by Status</DropdownMenuLabel>
                <DropdownMenuItem onClick={() => setStatusFilter('all')}>
                  All
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setStatusFilter('active')}>
                  Active
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setStatusFilter('inactive')}>
                  Inactive
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuLabel>Sort by</DropdownMenuLabel>
                <DropdownMenuItem onClick={() => setSortBy('name')}>
                  Company Name (A-Z)
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setSortBy('newest')}>
                  Newest First
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setSortBy('oldest')}>
                  Oldest First
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            
            <Button variant="outline" size="sm" disabled>
              <Download className="h-4 w-4 mr-1" />
              <span className="hidden sm:inline">Export</span>
            </Button>
          </div>
        </div>
        
        <TabsContent value="all" className="space-y-4">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Company</TableHead>
                    <TableHead>Owner</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Users</TableHead>
                    <TableHead>Invoices</TableHead>
                    <TableHead>Revenue</TableHead>
                    <TableHead>Donations ($)</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading && (
                    Array(5).fill(0).map((_, i) => (
                      <TableRow key={i}>
                        <TableCell><Skeleton className="h-6 w-32" /></TableCell>
                        <TableCell><Skeleton className="h-6 w-32" /></TableCell>
                        <TableCell><Skeleton className="h-6 w-20" /></TableCell>
                        <TableCell><Skeleton className="h-6 w-12" /></TableCell>
                        <TableCell><Skeleton className="h-6 w-12" /></TableCell>
                        <TableCell><Skeleton className="h-6 w-20" /></TableCell>
                        <TableCell><Skeleton className="h-6 w-20" /></TableCell>
                        <TableCell><Skeleton className="h-6 w-24" /></TableCell>
                        <TableCell><Skeleton className="h-6 w-20" /></TableCell>
                      </TableRow>
                    ))
                  )}
                  
                  {!isLoading && tenants?.map(tenant => (
                    <TableRow key={tenant.id} className="cursor-pointer hover:bg-muted/50" onClick={() => handleViewTenant(tenant.id)}>
                      <TableCell>
                        <div className="font-medium">{tenant.name}</div>
                        <div className="text-sm text-muted-foreground">{tenant.email}</div>
                      </TableCell>
                      <TableCell>
                        <div>{tenant.ownerName}</div>
                        <div className="text-sm text-muted-foreground">{tenant.ownerEmail}</div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={tenant.status === 'active' ? 'success' : 'secondary'}>
                          {tenant.status}
                        </Badge>
                      </TableCell>
                      <TableCell>{tenant.userCount}</TableCell>
                      <TableCell>{tenant.invoiceCount}</TableCell>
                      <TableCell>${tenant.revenueTotal.toLocaleString()}</TableCell>
                      <TableCell>${tenant.totalDonationsAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</TableCell>
                      <TableCell>{tenant.createdAt.toLocaleDateString()}</TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild onClick={e => e.stopPropagation()}>
                            <Button variant="ghost" size="sm">
                              Actions
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={(e) => {
                              e.stopPropagation();
                              handleViewTenant(tenant.id);
                            }}>
                              View Details
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={(e) => {
                                e.stopPropagation();
                                handleImpersonate(tenant.id);
                              }}
                              disabled={isImpersonationActive || impersonatingTenantId === tenant.id}
                            >
                              {impersonatingTenantId === tenant.id ? 'Starting...' : 'Impersonate'}
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              className={tenant.status === 'active' ? 'text-destructive' : 'text-green-600'}
                              onClick={(e) => {
                                e.stopPropagation();
                                // TODO: Toggle tenant status
                              }}
                            >
                              {tenant.status === 'active' ? 'Deactivate' : 'Activate'} Tenant
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                  
                  {!isLoading && tenants?.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} className="h-24 text-center">
                        No tenants found
                        {searchQuery && (
                          <p className="text-sm text-muted-foreground mt-1">
                            Try adjusting your search or filters
                          </p>
                        )}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </CardContent>
            <CardFooter className="border-t px-6 py-3">
              <Pagination>
                <PaginationContent>
                  <PaginationItem>
                    <PaginationPrevious 
                      href="#" 
                      onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                        e.preventDefault();
                        setCurrentPage(prev => Math.max(1, prev - 1));
                      }}
                      className={currentPage === 1 ? 'pointer-events-none opacity-50' : ''}
                    />
                  </PaginationItem>
                  {[...Array(3)].map((_, i) => {
                    const page = i + 1;
                    return (
                      <PaginationItem key={page}>
                        <PaginationLink 
                          href="#" 
                          isActive={page === currentPage}
                          onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                            e.preventDefault();
                            setCurrentPage(page);
                          }}
                        >
                          {page}
                        </PaginationLink>
                      </PaginationItem>
                    );
                  })}
                  <PaginationItem>
                    <PaginationEllipsis />
                  </PaginationItem>
                  <PaginationItem>
                    <PaginationNext 
                      href="#" 
                      onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
                        e.preventDefault();
                        setCurrentPage(prev => prev + 1);
                      }}
                    />
                  </PaginationItem>
                </PaginationContent>
              </Pagination>
            </CardFooter>
          </Card>
        </TabsContent>
        
        <TabsContent value="active" className="space-y-4">
          {/* Content identical to "all" but filtered to active tenants */}
          <Card>
            <CardContent className="pt-6">
              <p className="text-center text-muted-foreground">
                Showing active tenants only
              </p>
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="inactive" className="space-y-4">
          {/* Content identical to "all" but filtered to inactive tenants */}
          <Card>
            <CardContent className="pt-6">
              <p className="text-center text-muted-foreground">
                Showing inactive tenants only
              </p>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default TenantDashboard; 